

import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Trash2,
  Plus,
  Minus,
  ShoppingBag,
  ArrowRight,
  AlertCircle,
  Tag,
  CheckCircle,
  X,
  Loader2,
} from "lucide-react";

import { useCart, useClearCart } from "@/hooks/useCart";
import { useValidateCoupon } from "@/hooks/useCoupon";
import { useAuth } from "@/contexts/AuthContext";
import {
  formatPrice,
  calculateOrderTotals,
  ProductType,
} from "@bookstore/shared";
import { useState } from "react";
import { toast } from "sonner";
import Placeholder from '@/assets/placeholder.webp';
import CartSkeleton from "@/components/ui/CartSkeleton";

const CartPage = () => {
  const { user } = useAuth();
  const { cartWithDetails, totalPrice, updateQuantity, removeFromCart, isPending } = useCart();
  const clearCartMutation = useClearCart();
  const validateCouponMutation = useValidateCoupon();

  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discount: number;
    couponId: string;
  } | null>(null);

  const subtotal = totalPrice;
  const discount = appliedCoupon?.discount || 0;
  const shippingCharges = subtotal >= 500 ? 0 : 50;
  const totals = calculateOrderTotals(subtotal, discount, shippingCharges, 0);

  const handleClearCart = () => {
    if (window.confirm("Are you sure you want to clear your cart?")) {
      clearCartMutation.mutate();
      // Clear coupon when cart is cleared
      setAppliedCoupon(null);
      setCouponCode("");
    }
  };

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    
    if (!code) {
      return toast.error("Please enter a coupon code");
    }

    if (appliedCoupon) {
      return toast.info("Remove current coupon before applying a new one");
    }

    if (!user?.uid) {
      return toast.error("Please login to apply coupons");
    }

    try {
      const result = await validateCouponMutation.mutateAsync({
        code: code.toUpperCase(),
        orderAmount: subtotal,
        userId: user.uid,
      });

      if (result.valid && result.discount && result.couponId) {
        setAppliedCoupon({
          code: code.toUpperCase(),
          discount: result.discount,
          couponId: result.couponId,
        });
        toast.success(result.message || `🎉 Coupon applied! You saved ${formatPrice(result.discount)}`);
      } else {
        toast.error(result.message || "Invalid coupon code");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to validate coupon");
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    toast.info("Coupon removed");
  };

  // loading skeleton
  if (isPending) {
    return <CartSkeleton />;
  }

  // EMPTY CART UI
  if (cartWithDetails.length === 0) {
    return (
      <>
        <Helmet>
          <title>Shopping Cart | Bookstore</title>
        </Helmet>

        <div className="min-h-[80vh] flex flex-col items-center justify-center text-center bg-background-muted">
          <ShoppingBag className="w-24 h-24 text-primary-300 mb-6" />
          <h2 className="text-3xl font-bold text-text mb-3">Your cart is empty</h2>
          <p className="text-text-secondary mb-8">
            Looks like you haven't added anything yet.
          </p>

          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-xl font-medium shadow-md hover:bg-primary-700 transition-transform hover:-translate-y-0.5"
          >
            Start Shopping <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Shopping Cart | Bookstore</title>
      </Helmet>

      <div className="min-h-screen bg-background-muted/60 pb-20">
        <div className="container-custom py-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-10">
            <h1 className="text-3xl font-extrabold tracking-tight text-text">
              Your Cart{" "}
              <span className="text-text-secondary text-lg font-normal">
                ({cartWithDetails.length}{" "}
                {cartWithDetails.length === 1 ? "item" : "items"})
              </span>
            </h1>

            <button
              onClick={handleClearCart}
              disabled={clearCartMutation.isPending}
              className="text-sm text-error hover:text-red-700 font-medium flex items-center gap-1 transition"
            >
              <Trash2 className="w-4 h-4" />
              Clear Cart
            </button>
          </div>

          <div className="grid lg:grid-cols-3 gap-10">
            {/* LEFT SECTION — Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cartWithDetails.map((item) => {
                const product = item.product;
                const out = product.stock === 0;
                const over = item.quantity > product.stock;

                return (
                  <div
                    key={item.productId}
                    className={`
                      group relative rounded-2xl border p-5 shadow-sm transition-all
                      bg-white border-border hover:shadow-md
                      ${out || over ? "border-error/40 bg-error/5" : ""}
                    `}
                  >
                    <div className="flex gap-6">
                      {/* IMAGE */}
                      <div className="w-28 h-36 rounded-xl bg-background-muted overflow-hidden flex-shrink-0">
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.title}
                            className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-3xl">
                            <img
                              src={Placeholder}
                              alt="Placeholder"
                              className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
                            />
                          </div>
                        )}
                      </div>

                      {/* DETAILS */}
                      <div className="flex-1 flex flex-col justify-between">
                        <div>
                          <Link
                            to={`/product/${product.seoUrl}`}
                            className="text-lg font-semibold text-text hover:text-primary-600 transition"
                          >
                            {product.title}
                          </Link>

                          <p className="text-sm text-text-secondary mt-1">
                            {product.productType === ProductType.BOOK
                              ? product.author
                              : product.brand}
                          </p>

                          {/* STOCK WARNINGS */}
                          {out && (
                            <p className="text-error text-sm flex items-center gap-1 mt-2">
                              <AlertCircle className="w-4 h-4" />
                              Out of stock
                            </p>
                          )}

                          {!out && over && (
                            <p className="text-primary-700 text-sm flex items-center gap-1 mt-2">
                              <AlertCircle className="w-4 h-4" />
                              Only {product.stock} available
                            </p>
                          )}
                        </div>

                        {/* QTY + PRICE */}
                        <div className="flex items-center justify-between mt-4">
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-3">
                            <div className="flex items-center rounded-xl border border-border bg-background-soft overflow-hidden">
                              <button
                                onClick={() =>
                                  updateQuantity(item.productId, item.quantity - 1)
                                }
                                disabled={item.quantity <= 1}
                                className="px-3 py-2 hover:bg-background-muted disabled:opacity-40"
                              >
                                <Minus className="w-4 h-4" />
                              </button>

                              <span className="px-4 font-medium text-text">
                                {item.quantity}
                              </span>

                              <button
                                onClick={() =>
                                  updateQuantity(item.productId, item.quantity + 1)
                                }
                                disabled={item.quantity >= product.stock}
                                className="px-3 py-2 hover:bg-background-muted disabled:opacity-40"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>

                            <button
                              onClick={() => removeFromCart(item.productId)}
                              className="text-text-secondary hover:text-error transition"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>

                          {/* Price */}
                          <div className="text-right">
                            <p className="text-lg font-bold text-text">
                              {formatPrice(
                                (product.discountedPrice || product.price) *
                                  item.quantity
                              )}
                            </p>

                            {product.discountedPrice && (
                              <p className="text-sm text-text-secondary line-through">
                                {formatPrice(product.price * item.quantity)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* RIGHT SECTION — ORDER SUMMARY */}
            <div className="lg:col-span-1">
              <div className="
                sticky top-24 rounded-2xl p-6 space-y-6 shadow-lg border border-primary-100  
                bg-background-soft backdrop-blur-sm
              ">
                <h2 className="text-xl font-semibold text-text border-b pb-3">
                  Order Summary
                </h2>

                <div className="space-y-3">
                  <SummaryRow label="Subtotal" value={formatPrice(subtotal)} />
                  <SummaryRow
                    label="Shipping"
                    value={
                      shippingCharges === 0 ? (
                        <span className="text-success font-medium">FREE</span>
                      ) : (
                        formatPrice(shippingCharges)
                      )
                    }
                  />
                  {appliedCoupon && (
                    <SummaryRow
                      label="Discount"
                      value={`- ${formatPrice(discount)}`}
                      highlight
                    />
                  )}
                </div>

                {/* COUPON */}
                <div className="border-t pt-5">
                  <label className="text-sm font-medium text-text-secondary flex items-center gap-2">
                    <Tag className="w-4 h-4" /> Coupon Code
                  </label>

                  {appliedCoupon ? (
                    /* Applied Coupon Display */
                    <div className="mt-2 p-3 rounded-xl bg-green-50 border border-green-200 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm font-semibold text-green-900">
                            {appliedCoupon.code}
                          </p>
                          <p className="text-xs text-green-700">
                            Saved {formatPrice(appliedCoupon.discount)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoveCoupon}
                        className="p-1 hover:bg-green-100 rounded-full transition"
                        title="Remove coupon"
                      >
                        <X className="w-4 h-4 text-green-700" />
                      </button>
                    </div>
                  ) : (
                    /* Coupon Input */
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        placeholder="Enter coupon"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        disabled={validateCouponMutation.isPending}
                        className="
                          flex-1 px-4 py-2 rounded-xl border border-border 
                          bg-white focus:border-primary-600 focus:ring-1 
                          focus:ring-primary-600 outline-none transition
                          disabled:opacity-50 disabled:cursor-not-allowed
                        "
                      />

                      <button
                        onClick={handleApplyCoupon}
                        disabled={validateCouponMutation.isPending || !couponCode.trim()}
                        className={`
                          px-5 py-2 rounded-xl text-sm font-medium transition-all
                          flex items-center gap-2
                          ${
                            validateCouponMutation.isPending || !couponCode.trim()
                              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                              : "bg-primary-600 text-white hover:bg-primary-700 shadow-sm"
                          }
                        `}
                      >
                        {validateCouponMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Applying
                          </>
                        ) : (
                          "Apply"
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* FREE SHIPPING REMINDER */}
                {subtotal < 500 && (
                  <div className="p-3 rounded-xl bg-primary-50 border border-primary-200 text-primary-800 text-sm">
                    Add {formatPrice(500 - subtotal)} more for free shipping
                  </div>
                )}

                {/* TOTAL */}
                <div className="border-t pt-5 flex items-center justify-between">
                  <span className="text-lg font-semibold text-text">Total</span>
                  <span className="text-3xl font-bold text-text">
                    {formatPrice(totals.total)}
                  </span>
                </div>

                {/* ACTIONS */}
                <div className="space-y-3">
                  <Link
                    to="/checkout"
                    state={{ appliedCoupon }} // Pass coupon to checkout
                    className="
                      w-full flex items-center justify-center gap-2 
                      bg-primary-600 text-white py-3 rounded-xl font-semibold
                      hover:bg-primary-700 transition-transform hover:-translate-y-0.5 shadow
                    "
                  >
                    Proceed to Checkout <ArrowRight className="w-5 h-5" />
                  </Link>

                  <Link
                    to="/products"
                    className="
                      w-full flex items-center justify-center gap-2 
                      bg-background-muted text-text py-3 rounded-xl 
                      font-medium hover:bg-background-soft transition
                    "
                  >
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartPage;

const SummaryRow = ({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}) => (
  <div className="flex justify-between text-text-secondary">
    <span>{label}</span>
    <span
      className={`font-medium ${
        highlight ? "text-green-600 font-semibold" : "text-text"
      }`}
    >
      {value}
    </span>
  </div>
);